import { z } from "zod";
import {
  CheckboxFilterCheckedSchema,
  CheckboxFilterTypeSchema,
  MustMatchAllOrMatchNoneSchema as MustMatchAllOrMatchNoneSchema,
  MustMatchAnyMatchNoneSchema,
  NumberRangeTypeFilterSchema,
  OptionalDateRangeSchema,
  TwoNumberRangeTypeFilterSchema,
} from "../common/common-filter-definitions-schema";
import {
  MUST_CONTAIN_FILTER,
  MUST_MATCH_ALL_FILTER,
  MUST_MATCH_ANY_FILTER,
  MUST_NOT_CONTAIN_FILTER,
} from "../common/filter-matchers";
import { UseClassFilterSchema } from "./use-class-schema";
import { TrafficCountFilterSchema } from "./traffic-count-schema";
import { AdoptedRoadFilterSchema } from "./adopted-road-schema";

// Tenure filter schema (example)
export const TenureFilterSchema = z
  .object({
    id: z.literal("tenure-uk"),
    values: z.array(
      z.object({
        key: z.enum(["freehold", "leasehold"]),
        checked: CheckboxFilterCheckedSchema,
      }),
    ),
    must: MustMatchAnyMatchNoneSchema,
  })
  .describe("Used to filter parcels by tenure type (freehold or leasehold)");

// --- Additional Opportunities Filter ---
export const AdditionalOpportunitiesFilterSchema = z
  .object({
    id: z.literal("additional-opportunities-id"),
    values: z.array(
      z.object({
        key: z.enum(["declassified_green_belt"]),
        checked: CheckboxFilterCheckedSchema,
      }),
    ),
    must: MustMatchAnyMatchNoneSchema,
  })
  .describe("Filter for parcels with additional development opportunities");

// --- Agricultural Land Classification Filter ---
export const AgriculturalLandClassificationFilterSchema = z
  .object({
    id: z.literal("agricultural-land-classification-id"),
    values: z.array(
      z.object({
        key: z.enum([
          "grade_1",
          "grade_2",
          "grade_3a",
          "grade_3",
          "grade_3b",
          "grade_4",
          "grade_5",
          "non_agricultural",
        ]),
        checked: CheckboxFilterCheckedSchema,
      }),
    ),
    must: MustMatchAnyMatchNoneSchema,
  })
  .describe("Filter parcels based on agricultural land classification");

// --- Article 4 Filter ---
export const Article4FilterSchema = z
  .object({
    id: z.literal("article-four-id"),
    values: z.array(
      z.object({
        key: z.enum(["article_4_ma", "article_4_hmo", "article_4_other", "article_4_unknown"]),
        checked: CheckboxFilterCheckedSchema,
      }),
    ),
    must: MustMatchAnyMatchNoneSchema,
  })
  .describe("Filter parcels with Article 4 directions applied");

// --- Company Status Filter ---
export const CompanyStatusFilterSchema = z
  .object({
    id: z.literal("company-status-id"),
    values: z.array(
      z.object({
        key: z.enum([
          "active",
          "active_proposal_to_strike_off",
          "administration_order",
          "administrative_receiver",
          "in_administration",
          "in_administration_administrative_receiver",
          "in_administration_receiver_manager",
          "liquidation",
          "live_but_receiver_manager_on_at_least_one_charge",
          "receiver_manager_administrative_receiver",
          "receivership",
          "voluntary_arrangement",
          "voluntary_arrangement_administrative_receiver",
          "voluntary_arrangement_receiver_manager",
        ]),
        checked: CheckboxFilterCheckedSchema,
      }),
    ),
    must: MustMatchAnyMatchNoneSchema,
  })
  .describe("Filter parcels based on the status of the owning company");

// --- Constraints Adopted Filter ---
export const ConstraintsAdoptedFilterSchema = z
  .object({
    id: z.literal("adopted-constraints-id"),
    values: z.array(
      z.object({
        key: z.enum([
          "site_of_special_scientific_interest_(sssi)",
          "special_protection_area",
          "special_area_of_conservation",
          "ramsar_site",
          "national_nature_reserve",
          "local_nature_reserve",
          "common_land",
          "ancient_woodland",
          "environmental_constraint",
          "general_constraint",
          "biosphere_reserve",
          "heritage_coasts",
          "heritage_land_listed_buildings_outer",
          "flood_zone_2_env_agency",
          "flood_zone_3_env_agency",
          "very_low_risk_of_flooding_from_rivers_and_seas",
          "low_risk_of_flooding_from_rivers_and_seas",
          "medium_risk_of_flooding_from_rivers_and_seas",
          "high_risk_of_flooding_from_rivers_and_seas",
          "low_risk_of_flooding_from_surface_water",
          "medium_risk_of_flooding_from_surface_water",
          "high_risk_of_flooding_from_surface_water",
          "low_rofsw_climate_change",
          "medium_rofsw_climate_change",
          "high_rofsw_climate_change",
          "conservation_areas_outer",
          "employment_area_outer",
          "settlement_boundaries_outer",
          "green_belt_outer",
          "nutrient_neutrality",
          "protected_views_outer",
          "safe_guarding_outer",
          "metropolitan_open_land_outer",
          "open_space_outer",
          "article_4_ma",
          "article_4_hmo",
          "article_4_other",
          "article_4_unknown",
          "rights_of_way",
          "tree_preservation_orders",
          "national_landscape",
        ]),
        checked: CheckboxFilterCheckedSchema,
      }),
    ),
    must: MustMatchAllOrMatchNoneSchema,
    type: CheckboxFilterTypeSchema,
  })
  .describe("Filter for parcels with adopted constraints");

// --- Constraints Emerging Filter ---
export const ConstraintsEmergingFilterSchema = z.object({
  id: z.literal("emerging-constraints-id"),
  values: z.array(
    z.object({
      key: z.enum([
        "conservation_areas_outer",
        "employment_area_outer",
        "metropolitan_open_land_outer",
        "open_space_outer",
        "protected_areas_outer",
        "protected_views_outer",
        "safe_guarding_outer",
        "settlement_boundaries_outer",
      ]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  type: CheckboxFilterTypeSchema,
  must: MustMatchAllOrMatchNoneSchema,
});

// --- Grey Belt Favourability Filter ---
export const GreyBeltFavourabilityFilterSchema = z.object({
  id: z.literal("grey-belt-type-id"),
  values: z.array(
    z.object({
      key: z.enum([
        "grey_belt_land_more_favourable",
        "grey_belt_land_average_favourability",
        "grey_belt_land_less_favourable",
      ]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  type: CheckboxFilterTypeSchema,
  must: MustMatchAnyMatchNoneSchema,
});

// --- Land Availability Assessment Filter ---
export const LandAvailabilityAssessmentFilterSchema = z.object({
  id: z.literal("land-availability-assessment-id"),
  values: z.array(
    z.object({
      key: z.enum(["negative", "neutral", "positive"]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  must: MustMatchAnyMatchNoneSchema,
});

// --- Lease End Date Filter ---
export const LeaseEndDateFilterSchema = z.object({
  id: z.literal("lease-end-date"),
  type: z.literal("date-range"),
  range: OptionalDateRangeSchema,
});

// --- Lease Start Date Filter ---
export const LeaseStartDateFilterSchema = z.object({
  id: z.literal("lease-start-date"),
  type: z.literal("date-range"),
  range: OptionalDateRangeSchema,
});

// --- Ownership Type Filter ---
export const OwnershipTypeFilterSchema = z.object({
  id: z.literal("ownership-type-id"),
  values: z.array(
    z.object({
      key: z.enum(["corporate_body", "company", "council", "housing_association", "private"]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  must: MustMatchAnyMatchNoneSchema,
});

// --- Parcel Size Filter ---
export const ParcelSizeFilterSchema = z.object({
  id: z.literal("lot-area-id"),
  range: TwoNumberRangeTypeFilterSchema,
  type: NumberRangeTypeFilterSchema,
  rangeType: z.literal("area"),
});

// --- Owner Name Filter ---
export const OwnerNameFilterSchema = z.object({
  id: z.literal("proprietor-name"),
  must: z.enum([MUST_CONTAIN_FILTER, MUST_NOT_CONTAIN_FILTER]),

  text: z.string(),
});

// --- Property State Filter ---
export const PropertyStateFilterSchema = z.object({
  id: z.literal("property-state-id"),
  values: z.array(
    z.object({
      key: z.enum([
        "demolished",
        "in_use",
        "planning_permission_granted",
        "property_state",
        "under_construction",
        "unoccupied_vacant_derelict",
      ]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  must: MustMatchAnyMatchNoneSchema,
});

// --- Region Filter ---
export const RegionFilterSchema = z.object({
  id: z.literal("region-id"),
  region: z.string(), // GSS code
});

// --- Regeneration Zones Adopted Filter ---
export const RegenerationZonesAdoptedFilterSchema = z.object({
  id: z.literal("regeneration-zones-adopted-id"),
  values: z.array(
    z.object({
      key: z.enum(["area_action_plans", "named_regeneration_areas"]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  must: MustMatchAnyMatchNoneSchema,
});

// --- Regeneration Zones Emerging Filter ---
export const RegenerationZonesEmergingFilterSchema = z.object({
  id: z.literal("regeneration-zones-emerging-id"),
  values: z.array(
    z.object({
      key: z.enum(["area_action_plans", "named_regeneration_areas"]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  must: MustMatchAnyMatchNoneSchema,
});

// --- Search Location Filter ---
export const SearchLocationFilterSchema = z.object({
  id: z.literal("search-location-id"),
  location: z.any(), // Polygon or PointWithRadius, see OpenAPI for full structure
  must: z.literal("INTERSECT"),
});

// --- Site Allocations Adopted Filter ---
export const SiteAllocationsAdoptedFilterSchema = z.object({
  id: z.literal("site-allocations-id"),
  values: z.array(
    z.object({
      key: z.enum([
        "commercial_allocation",
        "community_allocation",
        "employment_allocation",
        "mixed_use_allocation",
        "residential_allocation",
        "uncategorised_allocation",
      ]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  must: MustMatchAnyMatchNoneSchema,
});

// --- Site Allocations Emerging Filter ---
export const SiteAllocationsEmergingFilterSchema = z.object({
  id: z.literal("site-allocations-emerging-id"),
  values: z.array(
    z.object({
      key: z.enum([
        "commercial_allocation",
        "community_allocation",
        "employment_allocation",
        "mixed_use_allocation",
        "residential_allocation",
        "uncategorised_allocation",
      ]),
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
  must: MustMatchAnyMatchNoneSchema,
});

// --- Developed Area Filter ---
export const DevelopedAreaFilterSchema = z.object({
  id: z.literal("developed-area-id"),
  range: TwoNumberRangeTypeFilterSchema,
  rangeType: z.literal("percentage"),
});

// --- Amenities Filter ---
export const AmenitiesTypeItemSchema = z.object({
  key: z.enum(["transport", "education", "retail", "healthcare"]),
  checked: CheckboxFilterCheckedSchema,
  items: z.array(
    z.object({
      key: z.string(), // amenity sub-type key
      checked: CheckboxFilterCheckedSchema,
    }),
  ),
});

export const AmenitiesTypeFilterSchema = z.object({
  id: z.literal("amenities-type-filter"),
  values: z.array(AmenitiesTypeItemSchema),
  must: MustMatchAllOrMatchNoneSchema,
  type: z.literal("nested-checkbox"),
});

export const AmenitiesDistanceFilterSchema = z.object({
  id: z.literal("amenities-distance-filter"),
  value: z.number(),
});

export const AmenitiesFilterSchema = z.object({
  id: z.literal("amenities-filter"),
  type: z.literal("compound"),
  filters: z.array(z.union([AmenitiesTypeFilterSchema, AmenitiesDistanceFilterSchema])),
  must: MustMatchAllOrMatchNoneSchema,
});

// --- Planning Applications Filter ---
export const PlanningApplicationsMultiTextFilterSchema = z.object({
  id: z.literal("planning-applications-multi-text-filter-id"),
  must: z.literal(MUST_MATCH_ALL_FILTER),
  searchTags: z.array(z.string()),
});

export const PlanningApplicationsSubmissionDateFilterSchema = z.object({
  id: z.literal("planning-applications-submission-date"),
  range: z.array(z.string().nullable()).min(2).max(2),
});

export const PlanningApplicationsNumberOfUnitsFilterSchema = z.object({
  id: z.literal("planning-applications-number-of-units"),
  range: TwoNumberRangeTypeFilterSchema,
  rangeType: z.literal("basic"),
});

export const PlanningApplicationsFilterSchema = z.object({
  id: z.literal("planning-applications-id"),
  type: z.literal("compound"),
  filters: z
    .array(
      z.union([
        PlanningApplicationsMultiTextFilterSchema,
        PlanningApplicationsSubmissionDateFilterSchema,
        PlanningApplicationsNumberOfUnitsFilterSchema,
      ]),
    )
    .min(3)
    .max(3),
  must: MustMatchAllOrMatchNoneSchema,
});

// --- Power Distance Filter ---
export const PowerDistanceTypeItemSchema = z.object({
  key: z.string(),
  checked: CheckboxFilterCheckedSchema,
});

export const PowerDistanceTypeFilterSchema = z.object({
  id: z.literal("power-type-id"),
  must: z.literal(MUST_MATCH_ALL_FILTER),
  type: z.literal("nested-checkbox"),
  values: z.array(PowerDistanceTypeItemSchema),
});

export const PowerDistanceFilterValueSchema = z.object({
  id: z.literal("power-distance-filter"),
  type: z.literal("number"),
  value: z.number(),
});

export const PowerDistanceFilterSchema = z.object({
  id: z.literal("power-id"),
  type: z.literal("compound"),
  filters: z
    .array(z.union([PowerDistanceTypeFilterSchema, PowerDistanceFilterValueSchema]))
    .min(1)
    .max(2),
  must: z.literal(MUST_MATCH_ANY_FILTER),
});

export const UK_FILTER_DEFINITIONS = z.discriminatedUnion("id", [
  TenureFilterSchema,
  CompanyStatusFilterSchema,
  GreyBeltFavourabilityFilterSchema,
  OwnerNameFilterSchema,
  OwnershipTypeFilterSchema,
  PropertyStateFilterSchema,
  SiteAllocationsAdoptedFilterSchema,
  SiteAllocationsEmergingFilterSchema,

  RegenerationZonesAdoptedFilterSchema,
  RegenerationZonesEmergingFilterSchema,
  ConstraintsAdoptedFilterSchema,
  ConstraintsEmergingFilterSchema,
  AgriculturalLandClassificationFilterSchema,

  Article4FilterSchema,
  DevelopedAreaFilterSchema,

  ParcelSizeFilterSchema,
  LeaseEndDateFilterSchema,

  AmenitiesFilterSchema,

  AdditionalOpportunitiesFilterSchema,
  LandAvailabilityAssessmentFilterSchema,
  LeaseStartDateFilterSchema,

  PlanningApplicationsFilterSchema,
  PowerDistanceFilterSchema,
  UseClassFilterSchema,
  TrafficCountFilterSchema,
  AdoptedRoadFilterSchema,
]);

export const UK_FILTER_DEFINITIONS_ARRAY = z.array(UK_FILTER_DEFINITIONS);
